import React, { useState, useEffect } from 'react';
import { CheckCircle2, Shield, Phone, Calendar, Download } from 'lucide-react';
import ConsultationForm from '../components/ConsultationForm';
import ExitIntentPopup from '../components/ExitIntentPopup';
import MobilePhoneFAB from '../components/MobilePhoneFAB';
import CookieConsent from '../components/CookieConsent';
import NonprofitDownloadSection from '../components/NonprofitDownloadSection';
import { credentialBadges } from '../constants/credentialBadges';
import { 
  ProblemSection, 
  SolutionSection, 
  BenefitsSection, 
  TimelineSection, 
  SocialProofSection, 
  FAQSection 
} from '../components/sections';
import '../styles/landing.css';

const SwitchPage = () => {
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [showExitPopup, setShowExitPopup] = useState(false);
  const [exitPopupShown, setExitPopupShown] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 800) {
        setShowStickyCTA(true);
      } else {
        setShowStickyCTA(false);
      }
    };

    let pageLoadTime = Date.now();
    
    const handleMouseLeave = (e) => {
      const timeOnPage = Date.now() - pageLoadTime;
      const hasBeenOnPageLongEnough = timeOnPage > 5000;
      const isLeavingFromTop = e.clientY <= 0;
      const hasNotSeenPopup = !exitPopupShown && !showThankYou;
      const notAlreadyShowing = !showExitPopup;
      
      if (isLeavingFromTop && hasBeenOnPageLongEnough && hasNotSeenPopup && notAlreadyShowing) {
        setShowExitPopup(true);
        setExitPopupShown(true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mouseleave', handleMouseLeave);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [showThankYou, exitPopupShown, showExitPopup]);

  const handleFormSubmit = (email) => {
    setSubmittedEmail(email);
    setShowThankYou(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openCalendly = () => {
    if (window.Calendly) {
      window.Calendly.initPopupWidget({url: 'https://calendly.com/d/cxnm-px9-pj8/new-meeting'});
    }
  };

  if (showThankYou) {
    return (
      <div className="landing-page">
        <ThankYouSection email={submittedEmail} />
        <Footer credentialBadges={credentialBadges} />
      </div>
    );
  }

  return (
    <div className="landing-page">
      <ExitIntentPopup 
        isOpen={showExitPopup} 
        onClose={() => setShowExitPopup(false)}
        onSubmit={() => setShowExitPopup(false)}
      />
      <MobilePhoneFAB />

      {showStickyCTA && (
        <div className="sticky-cta-bar">
          <div className="sticky-cta-content">
            <span className="sticky-cta-text">Ready to stop wasting time on call centers?</span>
            <div className="sticky-cta-actions">
              <a href="tel:+18558088689" className="sticky-phone-link">
                <Phone size={18} />
                (855) 808-8689
              </a>
              <button className="sticky-cta-button" onClick={() => document.getElementById('hero-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}>
                Get Free Cost Comparison
              </button>
            </div>
          </div>
        </div>
      )}

      <Header credentialBadges={credentialBadges} />
      <HeroSection onFormSubmit={handleFormSubmit} openCalendly={openCalendly} />
      <ProblemSection />
      <SolutionSection />
      <BenefitsSection onFormSubmit={handleFormSubmit} />
      <TimelineSection />
      <SocialProofSection credentialBadges={credentialBadges} />
      <FAQSection />
      <NonprofitDownloadSection />
      <FinalCTASection onFormSubmit={handleFormSubmit} />
      <Footer credentialBadges={credentialBadges} />
      <CookieConsent />
    </div>
  );
};

const Header = ({ credentialBadges }) => (
  <header className="header">
    <div className="header-content">
      <a href="https://www.amplifyhrm.com" className="logo" target="_blank" rel="noopener noreferrer">
        <img 
          src="https://customer-assets.emergentagent.com/job_69a9ffbf-3b0d-4791-80d1-6eb6aacb68ee/artifacts/5dacw0dj_Amplify%20-%20Logo.jpg" 
          alt="Amplify HR Logo" 
          className="logo-img"
          loading="eager"
        />
      </a>
      <div className="header-badges">
        {credentialBadges.slice(0, 2).map((badge) => (
          <img key={badge.alt} src={badge.src} alt={badge.alt} className="header-badge-img" loading="eager" />
        ))}
      </div>
    </div>
  </header>
);

const HeroSection = ({ onFormSubmit, openCalendly }) => {
  return (
    <section className="hero-section">
      <div className="hero-content">
        <div className="hero-text">
          <div className="hero-badge-pill">
            <Shield size={18} />
            <span>THE SERVICE-FIRST PEO</span>
          </div>
          <h1 className="hero-headline">Stop Wasting Time on Call Centers</h1>
          <p className="hero-subheadline">
            No call centers, no runarounds, just direct access to <span className="emphasis-your">YOUR</span> team that cares and knows you—plus a free cost comparison that typically uncovers <span className="emphasis-savings">15% to 40% savings</span>.
          </p>
          
          <div className="hero-phone-cta">
            <Phone size={20} />
            <span>Prefer to talk? Call our team: </span>
            <a href="tel:+18558088689" className="phone-link">(855) 808-8689</a>
          </div>
        </div>
        
        <div className="hero-form" id="hero-form">
          <div className="form-trust-badges">
            <div className="form-trust-badge-item">
              <CheckCircle2 size={16} />
              <span>Certified</span>
            </div>
            <div className="form-trust-badge-item">
              <CheckCircle2 size={16} />
              <span>Accredited</span>
            </div>
            <div className="form-trust-badge-item">
              <CheckCircle2 size={16} />
              <span>Award Winning</span>
            </div>
          </div>
          <ConsultationForm onSubmit={onFormSubmit} darkMode source="switch" />
          <p className="form-trust-line">
            No-pressure consultation. Free cost comparison. Quotes in days, not weeks.
          </p>
          
          <div className="form-divider">
            <span>OR</span>
          </div>
          <button className="calendly-cta" onClick={openCalendly}>
            <Calendar size={20} />
            Schedule a 15-minute call now
          </button>
        </div>
      </div>
    </section>
  );
};

const FinalCTASection = ({ onFormSubmit }) => {
  const [showDownloadPopup, setShowDownloadPopup] = React.useState(false);

  const handleDownloadClick = (e) => {
    e.preventDefault();
    setShowDownloadPopup(true);
  };

  const handleDownloadSubmit = (email) => {
    setShowDownloadPopup(false);
  };

  return (
    <section className="final-cta-section">
      {showDownloadPopup && (
        <ExitIntentPopup 
          isOpen={showDownloadPopup}
          onClose={() => setShowDownloadPopup(false)}
          onSubmit={handleDownloadSubmit}
        />
      )}
      <div className="section-container">
      <h2 className="final-cta-headline">Every Day You Wait Costs You Time and Money</h2>
      <p className="final-cta-supporting-text">
        If your big box PEO still runs on tickets, hold times, and revolving reps, the problems keep piling up. Get a free, no-pressure consultation and see what switching could save you.
      </p>
      <div className="urgency-box">
        <Calendar size={18} />
        <span>Consultation slots are limited—schedule your free analysis today.</span>
      </div>
      <div className="final-cta-form">
        <ConsultationForm onSubmit={onFormSubmit} darkMode source="switch" />
        <p className="final-cta-trust-line">
          See your exact savings in 30 minutes • Get your quote in days, not weeks
        </p>
      </div>
      <div className="cta-divider">
        <span>OR</span>
      </div>
      <button 
        onClick={handleDownloadClick}
        className="secondary-cta-link"
      >
        <Download size={18} />
        <span>Download PEO Buyers Guide</span>
      </button>
      <p className="secondary-cta-microcopy">
        Get a clear side by side comparison you can share with your leadership team.
      </p>
    </div>
  </section>
);
};

const ThankYouSection = ({ email }) => {
  const handleDownloadClick = () => {
    window.open('https://customer-assets.emergentagent.com/job_69a9ffbf-3b0d-4791-80d1-6eb6aacb68ee/artifacts/5dacw0dj_Amplify%20-%20Logo.jpg', '_blank');
  };

  return (
    <section className="thankyou-section">
      <div className="thankyou-container">
        <div className="thankyou-icon">
          <CheckCircle2 size={64} />
        </div>
        <h1 className="thankyou-headline">You're One Step Closer to Better HR</h1>
        <p className="thankyou-subheadline">
          Thanks! A PEO specialist will reach out shortly with your personalized cost analysis.
        </p>
        <p className="thankyou-email">
          Confirmation sent to: <strong>{email}</strong>
        </p>
        
        <div className="thankyou-next-steps">
          <h2 className="thankyou-next-title">What Happens Next</h2>
          <div className="thankyou-steps">
            <div className="thankyou-step">
              <div className="thankyou-step-number">1</div>
              <h3 className="thankyou-step-title">We review your current setup</h3>
              <p className="thankyou-step-description">Our team looks at your current PEO arrangement and identifies cost and service gaps.</p>
            </div>
            <div className="thankyou-step">
              <div className="thankyou-step-number">2</div>
              <h3 className="thankyou-step-title">We prepare a detailed cost analysis</h3>
              <p className="thankyou-step-description">You get a clear comparison showing potential savings and service improvements.</p>
            </div>
            <div className="thankyou-step">
              <div className="thankyou-step-number">3</div>
              <h3 className="thankyou-step-title">We walk through it together</h3>
              <p className="thankyou-step-description">No pressure—just clarity on what switching could mean for your business.</p>
            </div>
          </div>
        </div>

        <div className="thankyou-download-section">
          <p className="thankyou-download-label">While you wait, grab our free guide:</p>
          <button onClick={handleDownloadClick} className="thankyou-download-button">
            <Download size={20} />
            Download PEO Buyers Guide
          </button>
        </div>

        <div className="thankyou-contact">
          <p>Questions? Call us directly:</p>
          <a href="tel:+18558088689" className="thankyou-phone">(855) 808-8689</a>
        </div>
      </div>
    </section>
  );
};

const Footer = ({ credentialBadges }) => (
  <footer className="footer">
    <div className="footer-content">
      <div className="footer-badges">
        {credentialBadges.map((badge) => (
          <div key={badge.alt} className="footer-badge">
            <img src={badge.src} alt={badge.alt} loading="lazy" />
          </div>
        ))}
      </div>
      <p className="footer-copyright">© 2026 Amplify HR. All rights reserved.</p>
    </div>
  </footer>
);

export default SwitchPage;
